<template>
  <div class="mt-4">
    <!-- 상단 -->
    <v-card class="mx-auto" max-width="95%" elevation="2">
      <v-toolbar dark dense>
        <v-toolbar-title>단타의 신 · 진행중</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn color="white" outlined small @click="$router.push('/danta_game_manage')">
          <v-icon left small>mdi-gamepad-variant-outline</v-icon>게임관리
        </v-btn>
      </v-toolbar>
    </v-card>

    <!-- 회차 (라운드) 탭 -->
    <v-card class="mx-auto mt-4" max-width="95%" elevation="2">
      <v-tabs v-model="roundTab" color="primary" grow>
        <v-tab>
          <v-icon left small>mdi-play-circle</v-icon>
          진행중인 회차
          <v-chip v-if="activeRounds.length > 0" small color="green" dark class="ml-2">{{ activeRounds.length }}</v-chip>
        </v-tab>
        <v-tab>
          <v-icon left small>mdi-history</v-icon>
          지난 회차
          <v-chip v-if="pastRounds.length > 0" small outlined class="ml-2">{{ pastRounds.length }}</v-chip>
        </v-tab>
      </v-tabs>
      <v-divider></v-divider>
      <v-tabs-items v-model="roundTab">

        <!-- 탭1: 진행중인 회차 -->
        <v-tab-item>
          <v-card-text class="pa-0">
            <div style="overflow-x: auto;">
            <v-data-table
              v-if="activeRounds.length > 0"
              :headers="activeRoundHeaders"
              :items="activeRounds"
              :items-per-page="-1"
              :footer-props="{'items-per-page-options':[20, 50, -1]}"
              class="elevation-0"
            >
              <template v-slot:item.status="{ item }">
                <v-chip small :color="roundStatusColor(item.status)" dark>
                  <v-icon left x-small>{{ roundStatusIcon(item.status) }}</v-icon>
                  {{ roundStatusLabel(item.status) }}
                </v-chip>
              </template>
              <template v-slot:item.symbol="{ item }">
                <span class="font-weight-medium">{{ item.symbol_name || item.symbol }}</span>
                <span class="text-caption grey--text ml-1">{{ item.symbol }}</span>
              </template>
              <template v-slot:item.started_at="{ item }">
                <span>{{ formatKST(item.started_at) }}</span>
              </template>
              <template v-slot:item.end_at="{ item }">
                <span>{{ formatKST(item.end_at) }}</span>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn x-small color="primary" depressed @click="openDetail(item)">
                  <v-icon left x-small>mdi-magnify</v-icon>상세
                </v-btn>
                <v-btn
                  x-small
                  color="red darken-1"
                  dark
                  depressed
                  class="ml-1"
                  @click="openForceSettle(item)"
                >
                  <v-icon left x-small>mdi-flash</v-icon>강제종료
                </v-btn>
              </template>
            </v-data-table>
            </div>
            <v-container v-if="activeRounds.length === 0" class="text-center py-10">
              <v-icon size="56" color="grey lighten-1">mdi-play-circle-outline</v-icon>
              <p class="text-body-1 grey--text mt-4">진행중인 회차가 없습니다.</p>
              <p class="text-caption grey--text">게임을 활성화하면 회차가 자동으로 생성됩니다.</p>
            </v-container>
          </v-card-text>
        </v-tab-item>

        <!-- 탭2: 지난 회차 -->
        <v-tab-item>
          <v-card-text class="pa-0">
            <div style="overflow-x: auto;">
            <v-data-table
              v-if="pastRounds.length > 0"
              :headers="pastRoundHeaders"
              :items="pastRounds"
              :items-per-page="20"
              :footer-props="{'items-per-page-options':[20, 50, -1]}"
              class="elevation-0"
            >
              <template v-slot:item.status="{ item }">
                <v-chip small color="grey" dark>종료</v-chip>
              </template>
              <template v-slot:item.symbol="{ item }">
                <span class="font-weight-medium">{{ item.symbol_name || item.symbol }}</span>
              </template>
              <template v-slot:item.started_at="{ item }">
                <span>{{ formatKST(item.started_at) }}</span>
              </template>
              <template v-slot:item.ended_at="{ item }">
                <span>{{ formatKST(item.ended_at) }}</span>
              </template>
              <template v-slot:item.result_direction="{ item }">
                <v-chip v-if="item.result_direction" small :color="item.result_direction === 'up' ? 'green' : item.result_direction === 'down' ? 'red' : 'grey'" dark>
                  {{ item.result_direction === 'up' ? '상승' : item.result_direction === 'down' ? '하락' : '동일' }}
                </v-chip>
                <span v-else class="grey--text">-</span>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn x-small color="primary" depressed @click="openDetail(item)">
                  <v-icon left x-small>mdi-magnify</v-icon>상세
                </v-btn>
              </template>
            </v-data-table>
            </div>
            <v-container v-if="pastRounds.length === 0" class="text-center py-10">
              <v-icon size="56" color="grey lighten-1">mdi-history</v-icon>
              <p class="text-body-1 grey--text mt-4">지난 회차 기록이 없습니다.</p>
            </v-container>
          </v-card-text>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- DEV 전용 — 임의 결과 강제 정산 다이얼로그 -->
    <v-dialog v-model="devDialog" max-width="520" persistent>
      <v-card>
        <v-toolbar dense color="red darken-1" dark flat>
          <v-icon left>mdi-flash</v-icon>
          <v-toolbar-title>라운드 강제 종료</v-toolbar-title>
        </v-toolbar>

        <v-card-text class="pt-4">
          <v-alert dense type="warning" border="left" class="text-caption mb-4">
            선택한 결과로 라운드를 <b>즉시 정산</b>합니다. 실제 경기/가격 결과와 무관하게 확정되며,
            베팅한 유저의 포인트가 <b>실제로 지급</b>됩니다. 되돌릴 수 없습니다.
            <br>라이브에서는 스포츠/manual 라운드만 가능합니다.
          </v-alert>

          <div v-if="devLoading && !devRound" class="text-center py-6">
            <v-progress-circular indeterminate color="primary" />
          </div>

          <div v-else-if="devRound">
            <v-simple-table dense class="mb-3">
              <tbody>
                <tr><td class="grey--text">회차ID</td><td>#{{ devRound.round_id }}</td></tr>
                <tr><td class="grey--text">게임</td><td>{{ devRound.game_title }}</td></tr>
                <tr><td class="grey--text">타입</td><td>{{ devRound.schedule_type }}<span v-if="devRound.bet_type"> / {{ devRound.bet_type }}</span></td></tr>
                <tr><td class="grey--text">상태</td><td>{{ devRound.status }}</td></tr>
                <tr><td class="grey--text">참가자 / 베팅</td><td>{{ devRound.total_players }}명 / {{ devRound.total_bet_amount }}</td></tr>
              </tbody>
            </v-simple-table>

            <v-alert v-if="forceBlockedOnLive" dense type="error" class="text-caption mb-3">
              라이브에서는 스포츠(event_driven)/manual 라운드만 강제 정산할 수 있습니다.
              이 라운드(타입: {{ devRound.schedule_type }})는 자동 정산만 가능합니다.
            </v-alert>

            <div v-if="!devRound.settleable">
              <v-alert dense type="error" class="text-caption">
                이미 {{ devRound.status }} 상태라 강제 정산할 수 없습니다.
              </v-alert>
            </div>
            <div v-else>
              <div class="text-subtitle-2 mb-1">정산 결과(승리 선택지)</div>
              <v-radio-group v-model="devChoice" dense class="mt-0">
                <v-radio
                  v-for="c in devRound.choices"
                  :key="c.code"
                  :label="`${c.label} (${c.code})`"
                  :value="c.code"
                />
              </v-radio-group>

              <v-text-field
                v-model="devReason"
                label="사유 (선택, 감사 로그용)"
                dense outlined hide-details class="mb-3"
                placeholder="예: QA 정산 플로우 테스트"
              />
              <v-checkbox
                v-model="devSkipCallback"
                dense hide-details class="mt-0"
                label="매체사 콜백(round_ended) 생략 — 순수 내부 테스트"
              />
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="devDialog = false" :disabled="devLoading">취소</v-btn>
          <v-btn
            color="red darken-1"
            dark
            :loading="devLoading"
            :disabled="!devRound || !devRound.settleable || !devChoice || forceBlockedOnLive"
            @click="confirmForceSettle"
          >
            강제 정산 실행
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="py-12"></div>
    <div class="py-12"></div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import config from '../../config/config';
export default {
  name: 'Danta_GameInProgress',
  data: () => ({
    roundTab: 0,
    activeRounds: [],
    pastRounds: [],
    // DEV 전용 — 임의 결과 강제 정산
    devDialog: false,
    devLoading: false,
    devRound: null,        // { round_id, game_title, schedule_type, status, choices: [...], ... }
    devChoice: null,       // 선택한 승리 choice code
    devReason: '',
    devSkipCallback: false,
    activeRoundHeaders: [
      { text: '회차ID', value: 'round_id', width: '70px' },
      { text: '게임명', value: 'game_title', width: '180px' },
      { text: '종목', value: 'symbol', width: '140px' },
      { text: '회차번호', value: 'round_number', width: '80px' },
      { text: '참가자', value: 'total_players', width: '70px' },
      { text: '시작시각', value: 'started_at', width: '150px' },
      { text: '종료시각', value: 'end_at', width: '150px' },
      { text: '상태', value: 'status', width: '90px' },
      { text: '상세', value: 'actions', width: '100px', sortable: false, align: 'center' },
    ],
    pastRoundHeaders: [
      { text: '회차ID', value: 'round_id', width: '70px' },
      { text: '게임명', value: 'game_title', width: '180px' },
      { text: '종목', value: 'symbol', width: '120px' },
      { text: '회차번호', value: 'round_number', width: '80px' },
      { text: '참가자', value: 'total_players', width: '70px' },
      { text: '시작', value: 'started_at', width: '140px' },
      { text: '종료', value: 'ended_at', width: '140px' },
      { text: '결과', value: 'result_direction', width: '80px' },
      { text: '상태', value: 'status', width: '80px' },
      { text: '상세', value: 'actions', width: '100px', sortable: false, align: 'center' },
    ],
  }),
  computed: {
    // 라이브에서는 sports(event_driven)/manual 라운드만 강제 정산 허용 (백엔드 _assertForceAllowed 와 동일).
    // 가격형(24h/kr_market/us_market) 라운드는 라이브에서 실행을 막는다. dev/local 은 모두 허용.
    forceBlockedOnLive() {
      if (!config.isLive || !this.devRound) return false;
      const allowed = ['event_driven', 'manual'];
      return !allowed.includes(this.devRound.schedule_type);
    },
  },
  created() {
    this.loadRounds();
  },
  methods: {
    ...mapActions([
      'DANTA_GET_GAME_ROUND_LIST',
      'DANTA_DEV_ROUND_INFO',
      'DANTA_DEV_FORCE_RESULT',
    ]),
    async loadRounds() {
      try {
        const [activeData, pastData] = await Promise.all([
          this.DANTA_GET_GAME_ROUND_LIST({ status: 'active', page: 1, page_size: 100000 }),   // 진행중은 전체 로드 (백엔드 버전 무관하게 동작하도록 큰 수)
          this.DANTA_GET_GAME_ROUND_LIST({ status: 'ended', page: 1, page_size: 50 }),
        ]);
        this.activeRounds = activeData.round_list || [];
        this.pastRounds = pastData.round_list || [];
      } catch (err) { console.error('회차 로드 실패:', err); }
    },
    openDetail(item) {
      this.$router.push({ path: '/danta_game_round_detail', query: { round_id: item.round_id } });
    },
    // ── DEV 전용 — 임의 결과 강제 정산 ──
    async openForceSettle(item) {
      this.devRound = null;
      this.devChoice = null;
      this.devReason = '';
      this.devSkipCallback = false;
      this.devDialog = true;
      this.devLoading = true;
      try {
        const info = await this.DANTA_DEV_ROUND_INFO({ round_id: item.round_id });
        this.devRound = info || null;
        // 기본 선택 — 첫 번째 choice
        if (this.devRound && Array.isArray(this.devRound.choices) && this.devRound.choices.length > 0) {
          this.devChoice = this.devRound.choices[0].code;
        }
      } catch (err) {
        const msg = (typeof err === 'string') ? err : (err && err.message) ? err.message : JSON.stringify(err);
        console.error('라운드 정보 로드 실패:', err);
        alert('라운드 정보를 불러오지 못했습니다.\n사유: ' + msg);
        this.devDialog = false;
      } finally {
        this.devLoading = false;
      }
    },
    async confirmForceSettle() {
      if (!this.devRound) return;
      if (!this.devChoice) { alert('정산 결과(승리 선택지)를 선택해주세요.'); return; }
      const label = (this.devRound.choices.find(c => c.code === this.devChoice) || {}).label || this.devChoice;
      if (!confirm(`회차 #${this.devRound.round_id} 을(를) "${label}" 결과로 즉시 정산합니다.\n베팅한 유저의 포인트가 실제로 지급됩니다. 진행할까요?`)) return;
      this.devLoading = true;
      try {
        const out = await this.DANTA_DEV_FORCE_RESULT({
          round_id: this.devRound.round_id,
          choice_code: this.devChoice,
          reason: this.devReason || 'dev_force_settle',
          skip_callback: this.devSkipCallback,
        });
        const s = (out && out.settlement) || {};
        alert(`강제 정산 완료\n결과: ${this.devChoice}\n승자 ${s.winners ?? 0} / 패자 ${s.losers ?? 0} / 환불 ${s.refunded ?? 0}\n총 지급 ${s.totalPayout ?? 0}`);
        this.devDialog = false;
        this.loadRounds();
      } catch (err) {
        const msg = (typeof err === 'string') ? err : (err && err.message) ? err.message : JSON.stringify(err);
        console.error('강제 정산 실패:', err);
        alert('강제 정산에 실패했습니다.\n사유: ' + msg);
      } finally {
        this.devLoading = false;
      }
    },
    roundStatusColor(status) {
      const map = { betting: 'cyan', locked: 'orange', judging: 'purple', settled: 'blue-grey' };
      return map[status] || 'grey';
    },
    roundStatusLabel(status) {
      const map = { betting: '베팅중', locked: '마감', judging: '판정중', settled: '정산완료' };
      return map[status] || status;
    },
    roundStatusIcon(status) {
      const map = {
        betting: 'mdi-cash-multiple',
        locked: 'mdi-lock',
        judging: 'mdi-scale-balance',
        settled: 'mdi-check-circle',
      };
      return map[status] || 'mdi-help-circle-outline';
    },
    formatKST(value) {
      if (!value) return '-';
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      const parts = new Intl.DateTimeFormat('ko-KR', {
        timeZone: 'Asia/Seoul',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
      }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
      return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
    },
  },
};
</script>
